import React from 'react';
import {
    Calendar,
    MapPin,
    Eye,
    Clock,
    FileText,
    Folder,
    Star,
} from 'lucide-react';

const ProjectCard = ({ project, dropdownRef }) => {
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'en cours':
                return 'bg-emerald-500';
            case 'planifié':
                return 'bg-blue-500';
            case 'terminé':
                return 'bg-gray-500';
            default:
                return 'bg-gray-400';
        }
    };

    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`w-4 h-4 ${index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                    }`}
                aria-label={`Star ${index + 1}`}
            />
        ));
    };

    return (
        <div className="transition-shadow duration-300 bg-white border border-gray-200 shadow-sm rounded-2xl hover:shadow-lg">
            <div className="p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                            {project.module_name}
                        </h3>
                        <p className="text-xs italic text-gray-500">Ref: {project.reference}</p>
                    </div>

                    <div ref={dropdownRef}>
                        <a
                            href={`https://projets.forma-fusion.com/cfp/projets/${project.id}/detail`}
                            className="p-2 transition-colors rounded-full hover:bg-gray-100"
                            aria-label="View project details" target="_blank"
                        >
                            <Eye className="w-6 h-6 text-gray-600" />
                        </a>
                    </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2">
                    <div className="flex">{renderStars(project.rating || 0)}</div>
                    <span className="text-sm text-gray-600">
                        {project.rating || 0}.0 ({project.checkEval} reviews)
                    </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                    <span className="px-2.5 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {project.type}
                    </span>
                    <span className="px-2.5 py-1 text-xs rounded-full bg-cyan-100 text-cyan-800">
                        {project.modalite}
                    </span>
                    <span className="px-2.5 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                        {project.paiement}
                    </span>
                    <span
                        className={`px-2.5 py-1 text-xs text-white rounded-full ${getStatusColor(
                            project.status
                        )}`}
                    >
                        {project.status}
                    </span>
                </div>

                {/* Location */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">
                        {project.salle_name}, {project.li_name}, {project.salle_quartier ? project.salle_quartier : '--'}, {project.ville} ({project.salle_code_postal})
                    </span>
                </div>

                {/* Folder */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Folder className="w-4 h-4" />
                    <span>Aucun doucument</span>
                </div>

                {/* Avatars & Company */}
                <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                        {[...Array(3)].map((_, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-center w-8 h-8 text-xs font-semibold text-gray-600 bg-gray-200 rounded-full ring-2 ring-white"
                            >
                                C
                            </div>
                        ))}
                    </div>
                    <div className="w-12 h-8 bg-gray-200 rounded-lg" />
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{project.totalSessionHour}H</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{project.seanceCount} Sessions</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{project.nbDocument} Docs</span>
                    </div>
                </div>

                {/* Footer */}
                <div className="grid grid-cols-4 gap-4 pt-4 text-sm border-t border-gray-100">
                    <div className="grid grid-cols-2 col-span-2 gap-4">
                        <div>
                            <h6 className="text-xs text-gray-500 uppercase">Start</h6>
                            <p className="font-semibold text-gray-800">{project.startDate}</p>
                        </div>
                        <div>
                            <h6 className="text-xs text-gray-500 uppercase">End</h6>
                            <p className="font-semibold text-gray-800">{project.endDate}</p>
                        </div>
                    </div>
                    <div>
                        <h6 className="text-xs text-gray-500 uppercase">Price HT</h6>
                        <p className="font-bold text-gray-800">{project.total_ht}</p>
                    </div>
                    <div>
                        <h6 className="text-xs text-gray-500 uppercase">Payment</h6>
                        <div className="w-full h-6 bg-gray-100 rounded" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectCard;
